import React, { forwardRef, useState } from 'react'
import { Map as LeafletMap } from 'leaflet'
import { Dayjs } from 'dayjs'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import DeleteSharpIcon from '@mui/icons-material/DeleteSharp'
import EmptyBox from '@/javascript/presentation/pages/work_team_maps/components/empty-box/empty-box'
import RouteMapView from '@/javascript/presentation/pages/routing/components/route-map-view'
import { RouteItemSummaryList } from '@/javascript/presentation/pages/routing/create/distribution/route-summary-list'
import { RouteItem } from '@/javascript/domain/entities/maps/route-item'
import ErrorBox from '@/components/shared/boxes/ErrorBox'
import ConfirmationDialog from '@/javascript/presentation/components/modals/confirmation-dialog'

type Props = {
  isReady: boolean
  routes: RouteItem[]
  canCreateRoute: boolean
  deleteRoute: { delete: (id: number) => Promise<void> }
  date: Dayjs
  setRoutes: React.Dispatch<React.SetStateAction<RouteItem[]>>
}

const RoutesBox = forwardRef<LeafletMap, Props>(({ isReady, routes, canCreateRoute, deleteRoute, date, setRoutes }, ref) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const handleToggle = (index: number, isExpanded: boolean) => {
    setExpandedIndex(isExpanded ? index : null)
  }

  const handleDeleteRoute = async (routeId: number) => {
    setIsLoading(true)
    try {
      await deleteRoute.delete(routeId)
      const updated = routes.filter(route => route.id !== routeId)
      setRoutes(updated)
    } catch (error) {
      setError(error.message)
    } finally {
      setDialogOpen(false)
      setIsLoading(false)
    }
  }

  if (!isReady) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
        <CircularProgress />
      </Container>
    )
  }

  if (routes.length === 0) {
    return (
      <EmptyBox text="Não há rotas criadas para a data selecionada">
        {canCreateRoute && (
          <Button variant="contained" color="primary" size='medium'
            endIcon={<ArrowForwardRoundedIcon />}
            onClick={() => { window.location.href = '/routing/new' }}
          >
            Criar Rota
          </Button>
        )}
      </EmptyBox>
    )
  }

  return (
    <>
      {error &&
        <Container sx={{ m: 2 }}>
          <ErrorBox
            errorMessage={error}
            onClose={() => setError(null)}
            title="Erro ao excluir rota."
          />
        </Container>
      }
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row-reverse' }, width: '100%' }} data-testid="route-distribution">
        <Box sx={{ width: { xs: '100%', md: '60vw' }, height: { xs: 300, md: '70vh' } }}>
          <RouteMapView ref={ref} routes={routes} expandedIndex={expandedIndex} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, maxHeight: { xs: 400, md: '70vh' }, overflow: 'hidden' }}>
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <Box sx={{ pl: 5, pr: 5 }}>
              <RouteItemSummaryList
                routes={routes}
                expandedIndex={expandedIndex}
                onToggle={handleToggle}
              />
            </Box>
          </Box>
          { canCreateRoute && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mx: 5, my: 3 }}>
              <Button variant="contained" color="error" size='medium'
                startIcon={<DeleteSharpIcon />}
                onClick={() => setDialogOpen(true)}
                sx={{ textTransform: 'none' }}
                data-testid="delete-route-button"
              >
                Excluir rota
              </Button>
              <ConfirmationDialog
                modalOpen={dialogOpen}
                onClose={setDialogOpen}
                onSubmit={() => handleDeleteRoute(routes[0].id)}
                dialogTitleContent="Excluir rota"
                confirmationBtnProps={{
                  title: "Confirmar exclusão",
                  color: "error",
                  disabled: isLoading,
                  startIcon: isLoading ? <CircularProgress size={20} color="inherit" /> : null
                }}
                cancelBtnProps={{
                  title: "Fechar",
                  color: "inherit",
                  disabled: isLoading,
                }}
              >
                <Typography variant="body1">
                  Todas as rotas do <strong>dia {date.format('DD/MM/YYYY')}</strong> serão excluídas.
                </Typography>
                <Typography variant="body1" sx={{ mt: 2 }}>
                  Essa ação não poderá ser desfeita.
                </Typography>
              </ConfirmationDialog>
            </Box>
          ) }
        </Box>
      </Box>
    </>
  )
})

export default RoutesBox
